import { useFormik } from "formik";
import { toast } from "react-toastify";
import ModalComponent from "..";
import { useUpdateConfirmationEmailSettingsMutation } from "../../../redux/features/event/event.api";
import {
  ConfirmationEmailSettings,
  CreatedEvent,
} from "../../../redux/features/event/event.types";
import { ModalCompProps } from "../../../utils/types";
import { Button } from "../../buttons/button";
import { TextInput } from "../../inputs/textInput";
import { TokenTextarea } from "../../inputs/tokenTextarea";

const emailTokens = [
  { token: "{{eventName}}", label: "Event name" },
  { token: "{{firstName}}", label: "First name" },
  { token: "{{ticketName}}", label: "Ticket name" },
];

export const getDefaultSubject = (eventName: string) =>
  `New message in ${eventName ? "{{eventName}}" : "your event"}`;

export const ConfirmationEmailModal = ({
  open,
  handleClose,
  event,
  settings,
  onSuccess,
}: ModalCompProps & {
  event: CreatedEvent;
  settings: ConfirmationEmailSettings;
  onSuccess: () => void;
}) => {
  const [updateSettings, { isLoading: isSaving }] =
    useUpdateConfirmationEmailSettingsMutation();

  const {
    values,
    errors,
    touched,
    handleBlur,
    handleChange,
    handleSubmit,
    resetForm,
    setFieldValue,
  } = useFormik({
    enableReinitialize: true,
    initialValues: {
      subject: settings.subject,
      body: settings.body,
    },
    validate: (vals) => {
      const nextErrors: { body?: string } = {};

      if (!vals.body.trim()) {
        nextErrors.body = "Message is required";
      }

      return nextErrors;
    },
    onSubmit: async (vals) => {
      const payload = {
        enabled: true,
        subject: vals.subject.trim() || getDefaultSubject(event.name),
        body: vals.body.trim(),
      };

      const resp = await updateSettings({
        eventId: event._id,
        payload,
      });

      if (resp.data?.status === "success") {
        toast.success("Confirmation email saved");
        resetForm({ values: { subject: payload.subject, body: payload.body } });
        onSuccess();
        handleClose();
      }
    },
  });

  return (
    <ModalComponent
      open={open}
      handleClose={() => {
        resetForm();
        handleClose();
      }}
      showBackIcon={false}
      showIcon
      iconType="mail"
      mobileAnchorBottom
      closeOnOverlayClick
      title="Confirmation Email"
      subtitle="Guests would receive this email immediately after they get a ticket."
      className="md:!max-w-[620px]"
    >
      <form onSubmit={handleSubmit} className="mt-4 flex flex-col gap-4">
        <TextInput
          type="text"
          name="subject"
          value={values.subject}
          placeholder="New message in {{eventName}}"
          onChange={handleChange}
          onBlur={handleBlur}
          hasTitle
          title="Subject (Optional)"
          fill="grey"
        />

        <TokenTextarea
          name="body"
          value={values.body}
          placeholder="Enter message for your guests"
          onChange={(value) => setFieldValue("body", value)}
          onBlur={handleBlur}
          hasTitle
          title="Message"
          rows={5}
          fill="grey"
          errors={touched.body && errors.body ? errors.body : ""}
          tokens={emailTokens}
          helperText="You can insert variables by typing {"
        />

        <div className="flex justify-end">
          <Button
            text="Save"
            type="submit"
            loading={isSaving}
            disabled={isSaving}
            className="max-w-[112px]"
          />
        </div>
      </form>
    </ModalComponent>
  );
};
